import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

/**
 * Reusable prompt modal for text input (replaces window.prompt).
 * Matches the visual language of ConfirmationModal.jsx.
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - onSubmit: (value: string) => void  // fires with the trimmed value
 * - title: string
 * - message?: string | ReactNode        // helper copy above the input
 * - defaultValue?: string
 * - placeholder?: string
 * - confirmText?: string  (default "Save")
 * - cancelText?: string   (default "Cancel")
 * - type?: "info"|"success"|"warning"   (colour preset — default "info")
 * - icon?: string                        (emoji override — default per type)
 * - maxLength?: number                   (default 80)
 * - isProcessing?: boolean
 */
const PROMPT_TYPES = {
  info: {
    gradient: 'from-blue-400 via-blue-500 to-blue-600',
    ring: 'focus:ring-blue-500 focus:border-blue-500',
    button: 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700',
    border: 'border-blue-300 dark:border-blue-600',
    icon: '✎',
  },
  success: {
    gradient: 'from-green-400 via-green-500 to-emerald-500',
    ring: 'focus:ring-green-500 focus:border-green-500',
    button: 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600',
    border: 'border-green-300 dark:border-green-600',
    icon: '✨',
  },
  warning: {
    gradient: 'from-yellow-400 via-orange-500 to-amber-500',
    ring: 'focus:ring-orange-500 focus:border-orange-500',
    button: 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600',
    border: 'border-yellow-300 dark:border-yellow-600',
    icon: '⚠️',
  },
};

const PromptModal = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  message,
  defaultValue = '',
  placeholder = '',
  confirmText = 'Save',
  cancelText = 'Cancel',
  type = 'info',
  icon = null,
  maxLength = 80,
  isProcessing = false,
}) => {
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef(null);

  // Reset value each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setValue(defaultValue || '');
      // Focus + select the input a tick after mount so screen readers pick it up.
      setTimeout(() => {
        try { inputRef.current?.focus(); inputRef.current?.select(); } catch (_) {}
      }, 60);
    }
  }, [isOpen, defaultValue]);

  if (!isOpen) return null;

  const style = PROMPT_TYPES[type] || PROMPT_TYPES.info;
  const displayIcon = icon || style.icon;

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    const trimmed = (value || '').trim();
    if (!trimmed || isProcessing) return;
    onSubmit(trimmed);
  };

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fadeIn p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !isProcessing) onClose(); }}
      data-testid="prompt-modal-backdrop"
    >
      <div className="relative transform transition-all duration-300 ease-out animate-scaleIn">
        <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-md mx-4 border-2 ${style.border} relative overflow-hidden w-[92vw] sm:w-[26rem]`}>
          <div className="relative">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl bg-gradient-to-br ${style.gradient} text-white shadow-lg`}>
                {displayIcon}
              </div>
            </div>

            {/* Title */}
            <h3 className={`text-xl font-bold text-center mb-3 text-transparent bg-clip-text bg-gradient-to-r ${style.gradient}`}>
              {title}
            </h3>

            {message && (
              <p className="text-center text-sm text-gray-600 dark:text-gray-300 mb-4">
                {message}
              </p>
            )}

            <form onSubmit={handleSubmit}>
              <input
                ref={inputRef}
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={placeholder}
                maxLength={maxLength}
                disabled={isProcessing}
                data-testid="prompt-modal-input"
                className={`w-full px-4 py-3 rounded-xl border-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 outline-none focus:ring-2 ${style.ring} border-gray-200 dark:border-gray-700 disabled:opacity-50 mb-5`}
              />

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isProcessing}
                  data-testid="prompt-modal-cancel"
                  className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-all disabled:opacity-50"
                >
                  {cancelText}
                </button>
                <button
                  type="submit"
                  disabled={isProcessing || !value.trim()}
                  data-testid="prompt-modal-submit"
                  className={`flex-1 px-4 py-3 ${style.button} text-white rounded-xl font-medium transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:transform-none flex items-center justify-center gap-2`}
                >
                  {isProcessing ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Saving…
                    </>
                  ) : confirmText}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes scaleIn { from { transform: scale(0.95); opacity: 0 } to { transform: scale(1); opacity: 1 } }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out }
        .animate-scaleIn { animation: scaleIn 0.25s ease-out }
      `}</style>
    </div>
  );
  return typeof document !== 'undefined'
    ? ReactDOM.createPortal(modal, document.body)
    : modal;
};

export default PromptModal;
